import { useState } from 'react';
import axios from 'axios';
import { MapPin, Mail, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { API } from '../../App';

/**
 * Shown when a homeowner enters a ZIP code outside the current service area.
 * Collects an email so we can notify them when we expand into their area.
 *
 * @param {string} zipCode          The ZIP the customer entered.
 * @param {() => void} onChangeZip  Callback to let the user re-enter a ZIP.
 */
export const OutOfAreaWaitlist = ({ zipCode, onChangeZip }) => {
  const [email, setEmail] = useState('');
  const [zip, setZip] = useState(zipCode || '');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidZip = zip.length === 5 && /^\d{5}$/.test(zip);
  const canSubmit = isValidEmail && isValidZip && !submitting;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/waitlist`, { email, zip_code: zip });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-lg mx-auto text-center py-12" data-testid="waitlist-success">
        <div className="w-16 h-16 bg-[#7ED957]/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-[#7ED957]" />
        </div>
        <h1 className="font-manrope font-bold text-3xl text-[#1F2933] mb-3">
          You&apos;re on the list!
        </h1>
        <p className="text-[#1F2933]/70">
          We&apos;ll let you know as soon as ContractorHub becomes available in your area.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto py-8" data-testid="out-of-area-form">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-[#0FA3B1]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <MapPin className="w-8 h-8 text-[#0FA3B1]" />
        </div>
        <h1 className="font-manrope font-bold text-3xl text-[#1F2933] mb-3">
          We&apos;re not in your area yet.
        </h1>
        <p className="text-[#1F2933]/70">
          ContractorHub is currently serving the Greater Austin area, but we&apos;re expanding soon.
          Leave your email below and we&apos;ll let you know when ContractorHub becomes available in your area.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#1F2933] mb-2">
            Email Address
          </label>
          <div className="relative">
            <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="pl-9 py-6"
              data-testid="waitlist-email-input"
              autoFocus
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#1F2933] mb-2">
            ZIP Code
          </label>
          <div className="relative">
            <MapPin className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              type="text"
              inputMode="numeric"
              maxLength={5}
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
              placeholder="12345"
              className="pl-9 py-6"
              data-testid="waitlist-zip-input"
            />
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600" data-testid="waitlist-error">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={!canSubmit}
          className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl disabled:opacity-50"
          data-testid="waitlist-submit"
        >
          {submitting ? 'Submitting…' : 'Notify Me'}
        </Button>

        {onChangeZip && (
          <button
            type="button"
            onClick={onChangeZip}
            className="w-full text-sm text-[#1F2933]/60 hover:text-[#0FA3B1] mt-2"
            data-testid="waitlist-change-zip"
          >
            Use a different ZIP
          </button>
        )}
      </form>
    </div>
  );
};

export default OutOfAreaWaitlist;
